<template>
  <div class="tachogram-container-div">
    <div class="tachogram-header">
      <h2>Tachogramme</h2>

      <div class="header-controls-container" v-if="hasData">
        <div class="timeframe-selector">
          <div class="window-control">
            <button
              class="control-btn decrease"
              @click="decreaseWindow"
              :disabled="maxPoints <= 10"
            >
              <i class="fa fa-minus"></i>
            </button>
            <div class="time-value">{{ maxPoints }}s</div>
            <button class="control-btn increase" @click="increaseWindow">
              <i class="fa fa-plus"></i>
            </button>
          </div>
        </div>

        <!-- Affichage de la valeur actuelle sur mobile -->
        <div class="current-value mobile-bpm">
          <template v-if="exerciseStarted">
            <span class="value">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                style="vertical-align: middle"
              >
                <circle cx="12" cy="12" r="10" stroke="#fff" stroke-width="2" />
                <polyline
                  points="12,6 12,12 16,14"
                  stroke="#fff"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              {{ formatTimer(time) }}
            </span>
          </template>
          <template v-else>
            <span class="value">{{ latestBPM }}</span>
            <span class="unit">BPM</span>
          </template>
        </div>

        <button
          @click="toggleFullscreen"
          class="control-button fullscreen-btn"
          :title="isFullscreen ? 'Quitter le plein écran' : 'Plein écran'"
        >
          <svg
            v-if="!isFullscreen"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2 2h3M3 16h3a2 2 0 0 1 2 2v3"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
    </div>

    <!-- Informations complémentaires sur très petits écrans -->
    <div class="chart-info mobile-only" v-if="hasData">
      <div class="info-item">
        <span class="info-label">Actuel:</span>
        <span class="info-value">{{ latestBPM }} BPM</span>
      </div>
    </div>

    <div class="chart-container">
      <canvas id="tachogramChart"></canvas>
      <div v-if="!hasData" class="no-data-overlay">
        <div class="pulse-icon">
          <i class="fa fa-heartbeat"></i>
        </div>
        <p>En attente de données cardiaques...</p>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js";

export default {
  name: "Tachogram",
  props: {
    bpm: {
      type: Number,
      default: 0,
    },
    traceTacho: {
      type: Array,
      default: () => [],
    },
    isFullscreen: {
      type: Boolean,
      default: false,
    },
    time: {
      type: Number,
      default: 0,
    },
    exerciseStarted: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      chart: null,
      counter: 0,
      maxPoints: 60,
      chartData: {
        labels: [],
        datasets: [
          {
            label: "BPM",
            borderColor: "#489181",
            backgroundColor: "rgba(72, 145, 129, 0.15)",
            borderWidth: 2.5,
            pointRadius: 0,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: "#489181",
            pointHoverBorderColor: "#fff",
            pointHoverBorderWidth: 2,
            fill: true,
            lineTension: 0.3,
            data: [],
          },
        ],
      },
      options: {
        animation: {
          duration: 300,
          easing: "easeOutQuad",
        },
        maintainAspectRatio: false,
        responsive: true,
        scales: {
          yAxes: [
            {
              gridLines: {
                color: "rgba(200, 200, 200, 0.1)",
                zeroLineColor: "rgba(200, 200, 200, 0.2)",
                drawBorder: false,
                borderDash: [3, 3],
              },
              scaleLabel: {
                display: true,
                labelString: "BPM",
                fontColor: "#666",
                fontSize: this.getResponsiveFontSize(12),
                fontStyle: "bold",
              },
              ticks: {
                min: 30,
                max: 120,
                fontColor: "#888",
                padding: this.getResponsivePadding(10),
                fontSize: this.getResponsiveFontSize(10),
              },
            },
          ],
          xAxes: [
            {
              gridLines: {
                color: "rgba(200, 200, 200, 0.1)",
                zeroLineColor: "rgba(200, 200, 200, 0.2)",
                drawBorder: false,
                borderDash: [3, 3],
                offsetGridLines: true,
              },
              scaleLabel: {
                display: window.innerWidth > 480,
                labelString: "Temps (s)",
                fontColor: "#666",
                fontSize: this.getResponsiveFontSize(12),
                fontStyle: "bold",
              },
              ticks: {
                fontColor: "#888",
                padding: this.getResponsivePadding(10),
                maxRotation: window.innerWidth > 768 ? 0 : 45,
                fontSize: this.getResponsiveFontSize(10),
                maxTicksLimit: this.getMaxTicksLimit(),
              },
            },
          ],
        },
        legend: {
          display: false,
        },
        tooltips: {
          backgroundColor: "rgba(72, 145, 129, 0.9)",
          titleFontFamily: "'Arial', sans-serif",
          bodyFontFamily: "'Arial', sans-serif",
          cornerRadius: 6,
          xPadding: 10,
          yPadding: 10,
          displayColors: false,
          titleFontSize: this.getResponsiveFontSize(12),
          bodyFontSize: this.getResponsiveFontSize(11),
          callbacks: {
            title: function (tooltipItems) {
              return `Temps: ${tooltipItems[0].xLabel}`;
            },
            label: function (tooltipItem) {
              return `BPM: ${tooltipItem.yLabel.toFixed(0)}`;
            },
          },
        },
        hover: {
          mode: "nearest",
          intersect: false,
        },
        elements: {
          line: {
            tension: 0.4,
          },
        },
      },
      updateInterval: null,
    };
  },
  computed: {
    hasData() {
      return this.chartData.datasets[0].data.length > 0;
    },
    latestBPM() {
      if (!this.hasData) return "-";
      return Math.round(
        this.chartData.datasets[0].data[
          this.chartData.datasets[0].data.length - 1
        ]
      );
    },
  },
  watch: {
    bpm: {
      handler(newBPM) {
        if (newBPM > 0) {
          this.addDataPoint(newBPM);
        }
      },
    },
  },
  methods: {
    getResponsiveFontSize(baseSize) {
      if (window.innerWidth <= 480) return Math.max(8, baseSize - 2);
      if (window.innerWidth <= 768) return Math.max(9, baseSize - 1);
      return baseSize;
    },

    getResponsivePadding(basePadding) {
      if (window.innerWidth <= 480) return Math.max(4, basePadding - 4);
      if (window.innerWidth <= 768) return Math.max(6, basePadding - 2);
      return basePadding;
    },
    formatTimer(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    },
    getMaxTicksLimit() {
      if (window.innerWidth <= 480) return 6;
      if (window.innerWidth <= 768) return 8;
      return 12;
    },

    updateChartResponsiveness() {
      if (!this.chart) return;

      // Mise à jour des options responsives
      this.chart.options.scales.yAxes[0].scaleLabel.fontSize =
        this.getResponsiveFontSize(12);
      this.chart.options.scales.yAxes[0].ticks.fontSize =
        this.getResponsiveFontSize(10);
      this.chart.options.scales.yAxes[0].ticks.padding =
        this.getResponsivePadding(10);

      this.chart.options.scales.xAxes[0].scaleLabel.display =
        window.innerWidth > 480;
      this.chart.options.scales.xAxes[0].scaleLabel.fontSize =
        this.getResponsiveFontSize(12);
      this.chart.options.scales.xAxes[0].ticks.fontSize =
        this.getResponsiveFontSize(10);
      this.chart.options.scales.xAxes[0].ticks.padding =
        this.getResponsivePadding(10);
      this.chart.options.scales.xAxes[0].ticks.maxRotation =
        window.innerWidth > 768 ? 0 : 45;
      this.chart.options.scales.xAxes[0].ticks.maxTicksLimit =
        this.getMaxTicksLimit();

      this.chart.options.tooltips.titleFontSize =
        this.getResponsiveFontSize(12);
      this.chart.options.tooltips.bodyFontSize = this.getResponsiveFontSize(11);

      this.chart.update();
    },

    initChart() {
      const ctx = document.getElementById("tachogramChart").getContext("2d");
      if (ctx) {
        // Créer un dégradé pour le fond
        const gradient = ctx.createLinearGradient(0, 0, 0, 300);
        gradient.addColorStop(0, "rgba(72, 145, 129, 0.3)");
        gradient.addColorStop(1, "rgba(72, 145, 129, 0.02)");
        this.chartData.datasets[0].backgroundColor = gradient;

        this.chart = new Chart(ctx, {
          type: "line",
          data: this.chartData,
          options: this.options,
        });

        // Initialiser la responsivité
        this.updateChartResponsiveness();
        console.log("Chart initialized successfully");
      } else {
        console.error("Canvas context not found");
      }
    },

    toggleFullscreen() {
      this.$emit("toggle-fullscreen");
    },

    adjustYAxisRange() {
      if (this.chartData.datasets[0].data.length > 0) {
        const values = this.chartData.datasets[0].data;
        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);

        // Si les min/max sont trop proches, utiliser des valeurs par défaut
        if (maxValue - minValue < 10) {
          // Définir une plage minimale de 30 BPM
          const midPoint = (minValue + maxValue) / 2;
          const minRange = Math.max(30, Math.floor(midPoint - 15));
          const maxRange = Math.min(180, Math.ceil(midPoint + 15));

          this.chart.options.scales.yAxes[0].ticks.min = minRange;
          this.chart.options.scales.yAxes[0].ticks.max = maxRange;
          return;
        }

        // Sinon, ajuster en fonction des données réelles
        const range = maxValue - minValue;
        const padding = range * 0.2; // 20% padding

        const minRange = Math.max(30, Math.floor(minValue - padding));
        const maxRange = Math.min(180, Math.ceil(maxValue + padding));

        this.chart.options.scales.yAxes[0].ticks.min = minRange;
        this.chart.options.scales.yAxes[0].ticks.max = maxRange;
      }
    },

    addDataPoint(value) {
      // Créer une étiquette basée sur le compteur
      let label =
        this.counter < 60
          ? `${this.counter}s`
          : `${Math.floor(this.counter / 60)}:${(this.counter % 60)
              .toString()
              .padStart(2, "0")}`;

      this.counter++;

      // Ajouter les données au graphique
      this.chartData.labels.push(label);
      this.chartData.datasets[0].data.push(value);

      // Limiter le nombre de points affichés
      if (this.chartData.labels.length > this.maxPoints) {
        this.chartData.labels.shift();
        this.chartData.datasets[0].data.shift();
      }

      // Ajuster l'échelle Y si nécessaire
      this.adjustYAxisRange();

      // Mettre à jour le graphique
      if (this.chart) {
        this.chart.update();
      }
    },

    decreaseWindow() {
      if (this.maxPoints > 10) {
        this.maxPoints -= 10;
        this.updateWindow();
      }
    },

    increaseWindow() {
      this.maxPoints += 10;
      this.updateWindow();
    },

    updateWindow() {
      // Ajuster le nombre de points affichés
      while (this.chartData.labels.length > this.maxPoints) {
        this.chartData.labels.shift();
        this.chartData.datasets[0].data.shift();
      }

      if (this.chart) {
        this.chart.update();
      }
    },

    handleResize() {
      // Mettre à jour la responsivité lors du redimensionnement
      this.updateChartResponsiveness();
    },
  },
  mounted() {
    // Attendre que le DOM soit prêt
    this.$nextTick(() => {
      this.initChart();

      // Ajouter un listener pour le redimensionnement
      window.addEventListener("resize", this.handleResize);
    });
  },
  beforeDestroy() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }

    // Retirer le listener de redimensionnement
    window.removeEventListener("resize", this.handleResize);

    if (this.chart) {
      this.chart.destroy();
    }
  },
};
</script>

<style scoped>
.tachogram-container-div {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: white;
  padding: clamp(8px, 2vw, 16px);
  box-sizing: border-box;
}

.tachogram-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: clamp(10px, 3vw, 20px);
  padding-bottom: clamp(5px, 2vw, 10px);
  border-bottom: 1px solid rgba(200, 200, 200, 0.3);
  flex-wrap: wrap;
  gap: 10px;
}

h2 {
  margin: 0;
  color: #444;
  font-size: clamp(16px, 4vw, 20px);
  font-weight: 600;
  order: 1;
}

.header-controls-container {
  display: flex;
  align-items: center;
  gap: clamp(8px, 2vw, 15px);
  flex-wrap: wrap;
  order: 2;
  justify-content: flex-end;
}

.current-value {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: linear-gradient(to bottom right, #489181, #3a7a6d);
  color: white;
  padding: clamp(6px, 1.5vw, 8px) clamp(10px, 3vw, 15px);
  border-radius: clamp(8px, 2vw, 12px);
  box-shadow: 0 2px 8px rgba(72, 145, 129, 0.3);
  min-width: 60px;
}

.current-value .value {
  font-size: clamp(16px, 4vw, 20px);
  font-weight: bold;
  line-height: 1;
}

.current-value .unit {
  font-size: clamp(9px, 2.5vw, 11px);
  opacity: 0.8;
  color: white;
}

/* .mobile-bpm {
  display: none;
} */

.timeframe-selector {
  display: flex;
  align-items: center;
}

.window-control {
  display: flex;
  align-items: center;
  background-color: rgba(72, 145, 129, 0.1);
  border-radius: clamp(15px, 4vw, 20px);
  padding: clamp(2px, 1vw, 3px);
}

.control-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  width: clamp(20px, 5vw, 24px);
  height: clamp(20px, 5vw, 24px);
  border-radius: 50%;
  background-color: #489181;
  color: white;
  border: none;
  font-size: clamp(8px, 2.5vw, 10px);
  cursor: pointer;
  transition: all 0.2s ease;
}

.control-btn:hover:not(:disabled) {
  background-color: #3a7a6d;
  transform: scale(1.1);
}

.control-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.control-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.time-value {
  margin: 0 clamp(6px, 2vw, 8px);
  font-weight: 600;
  color: #444;
  min-width: clamp(30px, 8vw, 35px);
  text-align: center;
  font-size: clamp(10px, 3vw, 12px);
}

.chart-info {
  display: none;
  margin-bottom: 10px;
  padding: 8px;
  background-color: rgba(72, 145, 129, 0.05);
  border-radius: 6px;
  border-left: 3px solid #489181;
}

.chart-info .info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
  font-size: 12px;
}

.chart-info .info-label {
  color: #666;
  font-weight: 500;
}

.chart-info .info-value {
  color: #489181;
  font-weight: 600;
}
.chart-container {
  position: relative;
  min-height: clamp(110px, 22vh, 220px);
  max-height: 38vh;
  width: 100%;
  background-color: #fbfbfb;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.05);
  flex: 1;
}

#tachogramChart {
  width: 100%;
  height: 100%;
  padding: clamp(3px, 1vw, 5px);
}

.no-data-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(251, 251, 251, 0.9);
  border-radius: 6px;
}

.pulse-icon {
  font-size: clamp(24px, 6vw, 30px);
  color: #489181;
  margin-bottom: 10px;
  animation: pulse 1.5s infinite;
}

.no-data-overlay p {
  color: #666;
  font-style: italic;
  margin: 0;
  font-size: clamp(12px, 3vw, 14px);
  text-align: center;
  padding: 0 10px;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    opacity: 0.7;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.7;
  }
}

.control-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: clamp(8px, 2vw, 10px) clamp(12px, 3vw, 16px);
  border: none;
  border-radius: 8px;
  background-color: #f8f9fa;
  color: #495057;
  font-size: clamp(12px, 3vw, 14px);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.control-button:hover {
  background-color: #e9ecef;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.fullscreen-btn {
  padding: clamp(8px, 2vw, 10px);
}

.fullscreen-btn svg {
  width: clamp(16px, 4vw, 20px);
  height: clamp(16px, 4vw, 20px);
}

.mobile-only {
  display: none;
}

/* Media Queries pour une responsivité optimale */

/* Écrans très larges */
@media (min-width: 1200px) {
  .chart-container {
    max-height: 375px;
  }
}

/* Tablettes en mode paysage */
@media (max-width: 1024px) and (orientation: landscape) {
  .tachogram-header {
    margin-bottom: 15px;
  }

  .chart-container {
    min-height: 120px;
    max-height: 28vh;
  }
}

/* Tablettes en mode portrait */
@media (max-width: 768px) {
  .tachogram-header {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }

  h2 {
    text-align: center;
    order: 1;
  }

  .header-controls-container {
    order: 2;
    justify-content: center;
    flex-wrap: wrap;
  }

  .chart-container {
    min-height: 90px;
    max-height: 22vh;
  }

  .mobile-bpm {
    display: flex;
  }
}

/* Smartphones en mode paysage */
@media (max-width: 768px) and (orientation: landscape) and (max-height: 500px) {
  .tachogram-container-div {
    padding: 8px;
  }

  .tachogram-header {
    margin-bottom: 8px;
    padding-bottom: 5px;
  }

  .chart-container {
    min-height: 70px;
    max-height: 18vh;
  }

  .chart-info {
    padding: 6px;
    margin-bottom: 6px;
  }
}

/* Smartphones en mode portrait */
@media (max-width: 480px) {
  .tachogram-header {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 12px;
  }

  .header-controls-container {
    flex-direction: row;
    justify-content: space-around;
    width: 100%;
    flex-wrap: wrap;
    gap: 10px;
  }

  .timeframe-selector {
    order: 1;
    flex: 1;
    justify-content: center;
  }

  .mobile-bpm {
    order: 2;
    display: flex;
  }

  .fullscreen-btn {
    order: 3;
  }

  .window-control {
    width: 100%;
    justify-content: center;
    padding: 4px;
  }

  .chart-container {
    min-height: 60px;
    max-height: 14vh;
  }

  .mobile-only {
    display: block;
  }

  .chart-info .info-item {
    font-size: 11px;
    padding: 3px 0;
  }
}

/* Très petits écrans (< 350px) */
@media (max-width: 350px) {
  .tachogram-container-div {
    padding: 6px;
  }

  .header-controls-container {
    flex-direction: column;
    gap: 8px;
  }

  .timeframe-selector {
    width: 100%;
  }

  .chart-container {
    min-height: 40px;
    max-height: 10vh;
  }

  .chart-info {
    padding: 6px;
    font-size: 10px;
  }

  .chart-info .info-item {
    font-size: 10px;
  }
}

/* Mode plein écran */
.tachogram-container-div.fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 9999;
  background: white;
  padding: 20px;
}

.tachogram-container-div.fullscreen .chart-container {
  min-height: calc(100vh - 150px);
  max-height: calc(100vh - 150px);
}

/* Amélioration pour les écrans à haute densité */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .control-btn,
  .control-button {
    border: 0.5px solid rgba(0, 0, 0, 0.1);
  }
}

/* Support pour les préférences d'accessibilité */
@media (prefers-reduced-motion: reduce) {
  .control-btn,
  .control-button {
    transition: none;
  }

  .pulse-icon {
    animation: none;
  }
}

/* Mode sombre (si supporté par l'environnement) */
@media (prefers-color-scheme: dark) {
  .tachogram-container-div {
    background-color: #1a1a1a;
    color: #e0e0e0;
  }

  .chart-container {
    background-color: #2a2a2a;
  }

  h2 {
    color: #e0e0e0;
  }

  .control-button {
    background-color: #3a3a3a;
    color: #e0e0e0;
  }

  .control-button:hover {
    background-color: #4a4a4a;
  }
}
</style>
